import { Ban, ShieldUser, Trash2, UserCheck, UserMinus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PartnerRoleType, PartnerUserType } from '@/types/types_partnerUser';
import {
  callableErrorMessage,
  deletePartnerUser,
  setPartnerUserRole,
  setPartnerUserStatus,
} from '@/util/callable_functions';

/** Which of the two views this row is being rendered in. */
export type AccountManageModeType = 'admin' | 'owner';

interface AccountActionsProps {
  account: PartnerUserType;
  /**
   * 'admin' — the Eighty Days CMS: every action, on any account.
   * 'owner'  — a partner manager managing their own team: suspend and remove
   *            staff only. Role changes are absent because the server refuses
   *            them, so offering the button would be a lie.
   */
  mode: AccountManageModeType;
  /** Named in the confirmations, so a destructive click says which partner. */
  partnerName?: string;
  /** True when a *different* account is already this partner's manager. */
  hasOtherOwner?: boolean;
  onError: (message: string | null) => void;
}

// What a pending confirmation is about. One dialog driven by this beats three
// dialogs that can each be open independently.
type PendingActionType =
  | { kind: 'disable' }
  | { kind: 'role'; role: PartnerRoleType }
  | { kind: 'delete' };

/**
 * The per-account actions: suspend, restore, change role, remove.
 *
 * Every one of them goes through a callable that re-checks the caller, so this
 * component decides what to *offer*, never what is allowed. Enabling is the one
 * action with no confirmation — it only ever restores access.
 */
export function AccountActions({ account, mode, partnerName, hasOtherOwner, onError }: AccountActionsProps) {
  const [pending, setPending] = useState<PendingActionType | null>(null);
  // Which button is waiting, not merely that one is: Restore lives in the row and
  // the confirmations live in the dialog, so a single flag would spin both.
  const [running, setRunning] = useState<'restore' | 'confirm' | null>(null);
  const busy = running !== null;

  const who = account.displayName || account.email;
  const at = partnerName ? ` at ${partnerName}` : '';
  const isDisabled = account.status === 'disabled';
  const isOwner = account.role === 'owner';

  async function run(kind: 'restore' | 'confirm', action: () => Promise<unknown>) {
    setRunning(kind);
    onError(null);
    try {
      await action();
      setPending(null);
    } catch (e) {
      console.error('Account action failed', e);
      onError(callableErrorMessage(e));
      // Leave the dialog open: the message explains what to do instead, and
      // closing would hide the row the error is about.
    } finally {
      setRunning(null);
    }
  }

  // Copy for the confirmation, per action. Written out rather than assembled
  // from fragments — each one has a different consequence to spell out.
  function describePending(action: PendingActionType): { title: string; body: string; confirm: string } {
    switch (action.kind) {
      case 'disable':
        return {
          title: 'Suspend this account?',
          body: `${who} will be signed out of the partner portal${at} until the account is restored. Nothing is deleted, and ${isOwner ? 'they stay the partner’s manager' : 'they can be restored at any time'}.`,
          confirm: 'Suspend',
        };
      case 'role':
        return action.role === 'owner'
          ? {
              title: 'Make manager?',
              body: `${who} will be able to edit the partner profile${at} and invite staff. A partner has one manager, so this claims the role.`,
              confirm: 'Make manager',
            }
          : {
              title: 'Change to staff?',
              body: `${who} will lose the ability to edit the partner profile${at} and invite staff, and the manager role is freed for someone else.`,
              confirm: 'Change to staff',
            };
      case 'delete':
        return {
          title: 'Remove this account?',
          body: `${who} loses access to the partner portal${at} immediately. ${
            isOwner
              ? 'The manager role is freed, so a replacement can be invited.'
              : 'They can be invited again later.'
          } This cannot be undone — a new invitation is the only way back.`,
          confirm: 'Remove account',
        };
    }
  }

  function confirmPending() {
    if (!pending) return;
    switch (pending.kind) {
      case 'disable':
        return void run('confirm', () => setPartnerUserStatus({ uid: account.id, status: 'disabled' }));
      case 'role':
        return void run('confirm', () => setPartnerUserRole({ uid: account.id, role: pending.role }));
      case 'delete':
        return void run('confirm', () => deletePartnerUser({ uid: account.id }));
    }
  }

  const details = pending ? describePending(pending) : null;

  return (
    <>
      <div className='tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-1.5'>
        {isDisabled ? (
          <Button
            type='button'
            size='xs'
            variant='outline'
            disabled={busy}
            loading={running === 'restore'}
            title='Restore portal access'
            onClick={() => void run('restore', () => setPartnerUserStatus({ uid: account.id, status: 'active' }))}
          >
            <UserCheck /> Restore
          </Button>
        ) : (
          <Button
            type='button'
            size='xs'
            variant='outline'
            disabled={busy}
            title='Block sign-in without deleting the account'
            onClick={() => setPending({ kind: 'disable' })}
          >
            <Ban /> Suspend
          </Button>
        )}

        {/* Eighty Days only — `setPartnerUserRole` refuses a partner owner. */}
        {mode === 'admin' &&
          (isOwner ? (
            <Button
              type='button'
              size='xs'
              variant='outline'
              disabled={busy}
              title='Free the manager role for someone else'
              onClick={() => setPending({ kind: 'role', role: 'member' })}
            >
              <UserMinus /> Make staff
            </Button>
          ) : (
            <Button
              type='button'
              size='xs'
              variant='outline'
              disabled={busy || hasOtherOwner}
              title={
                hasOtherOwner
                  ? 'This partner already has a manager. Change that one to staff first.'
                  : 'Let this account edit the partner profile and invite staff'
              }
              onClick={() => setPending({ kind: 'role', role: 'owner' })}
            >
              <ShieldUser /> Make manager
            </Button>
          ))}

        <Button
          type='button'
          size='xs'
          variant='destructive'
          disabled={busy}
          onClick={() => setPending({ kind: 'delete' })}
        >
          <Trash2 /> Remove
        </Button>
      </div>

      <Dialog open={!!pending} onOpenChange={(open) => !open && !busy && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{details?.title}</DialogTitle>
            <DialogDescription>{details?.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={busy} onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              variant={pending?.kind === 'delete' ? 'destructive' : 'default'}
              loading={running === 'confirm'}
              onClick={confirmPending}
            >
              {busy ? 'Working…' : details?.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
